import { XCircle } from 'phosphor-react'
import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { Button } from '../../../../../components/Button'
import { DialogModal } from '../../../../../components/DialogModal'
import { SearchInput } from '../../../../../components/SearchInput'
import { type Column } from '../../../../../components/Table'
import { Medias } from '../../../../../enumns/medias'
import { useMediaQuery } from '../../../../../hooks/useMediaQuery'
import { ExpertForm } from './components/ExpertForm'
import { TableRowActions } from '../../components/TableRowActions'
import { TableWithInfiniteScroll } from './components/TableWithInfiniteScroll'
import { TableWithManuallyPagination } from './components/TableWithManuallyPagination'
import { type ExpertDomain } from '../../../../../types/domain/expert-domain'
import { type ExpertPersistence } from '../../../../../types/persistence/expert-persistence'
import { useExpertCreateFromAdminMutation } from '../../../../../services/hooks/experts/mutations/useExpertCreateMutation'
import { useExpertRemoveMutation } from '../../../../../services/hooks/experts/mutations/useExpertRemoveMutation'
import { useExpertEditMutation } from '../../../../../services/hooks/experts/mutations/useExpertEditMutation'
import { NavigationSelect } from '../../components/NavigationSelect'
import { TOAST_SUCCESS_DEFAULT_OPTIONS } from '../../../../../constants/toast'
import { type FiltersDomain } from '../../../../../types/domain/filters-domain'

export const Expert = () => {
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)

  const queryClient = useQueryClient()
  const expertCreationMutation = useExpertCreateFromAdminMutation()
  const expertRemotionMutation = useExpertRemoveMutation()
  const expertEditionMutation = useExpertEditMutation()

  const [expertToEdit, setExpertToEdit] = useState()
  const [formModalIsOpen, setFormModalIsOpen] = useState(false)
  const [filters, setFilters] = useState<FiltersDomain>({
    page: 1,
    take: isMinScreenWidthMD ? 10 : 20,
    search: '',
    hasAcceptedTerms: false,
    sort: 'createdAt',
    order: 'DESC',
    shouldShow: true,
  })

  const isFormInEditMode = Boolean(expertToEdit)

  const getCachedExpertById = (id: number) => {
    const expertsFromCache: any = queryClient.getQueryData(['experts', filters])

    if (isMinScreenWidthMD) {
      return expertsFromCache.data.find(
        (expert: ExpertDomain) => expert.id === id
      )
    }

    return expertsFromCache?.pages
      ?.reduce((acc: any, item: { data: ExpertDomain[] }) => {
        return [...acc, ...item.data]
      }, [])
      .find((expert: ExpertDomain) => expert.id === id)
  }

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      widthInPercentage: 10,
    },
    {
      field: 'name',
      headerName: 'Nome',
      widthInPercentage: 25,
    },
    {
      field: 'email',
      headerName: 'E-mail',
      widthInPercentage: 40,
    },
    {
      field: 'status',
      headerName: 'Status',
      widthInPercentage: 25,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      widthInPercentage: 10,
      renderCell ({ id }: { id: number }) {
        return (
          <TableRowActions
            tableContentType="Expert"
            isRemovingRegister={expertRemotionMutation.isLoading}
            alertMessage="Você está prestes a excluir um expert. Deseja continuar?"
            onEdit={() => {
              setExpertToEdit(getCachedExpertById(id))
              setFormModalIsOpen(true)
            }}
            onRemove={() => handleRemoveExpert(id)}
          />
        )
      },
    },
  ] as Column[]

  const createExpert = (data: ExpertPersistence) => {
    expertCreationMutation.mutate(data, {
      onSuccess: () => {
        toast.success(
          'Expert cadastrado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        setFormModalIsOpen(false)
        queryClient.invalidateQueries(['experts', filters])
      },
      onError: () => {
        toast.error('Erro ao cadastrar expert', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          closeButton: false,
          className: 'bg-red-50',
          icon: (
            <XCircle size={48} className="mx-auto my-auto text-alert-error" />
          ),
        })
      },
    })
  }

  const editExpert = (id: number, data: ExpertPersistence) => {
    expertEditionMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          toast.success(
            'Expert editado com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          setExpertToEdit(undefined)
          setFormModalIsOpen(false)
          queryClient.invalidateQueries(['experts', filters])
        },
        onError: () => {
          toast.error('Erro ao editar expert', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            className: 'bg-red-50',
            icon: (
              <XCircle size={48} className="mx-auto my-auto text-alert-error" />
            ),
          })
        },
      }
    )
  }

  const handleSaveExpert = (data: ExpertPersistence) => {
    if (isFormInEditMode) {
      editExpert((expertToEdit as any).id, data)
      return
    }

    createExpert(data)
  }

  const handleRemoveExpert = useCallback(
    (id: number) => {
      expertRemotionMutation.mutate(id, {
        onSuccess: () => {
          toast.success(
            'Expert deletado com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          setFormModalIsOpen(false)
          queryClient.invalidateQueries(['experts', filters])
        },
      })
    },
    [expertRemotionMutation]
  )

  const handleSearchExpert = (debouncedSearchTerm: string) =>
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: debouncedSearchTerm,
    }))

  const handleCloseFormModal = () => {
    if (isFormInEditMode) {
      setExpertToEdit(undefined)
    }

    setFormModalIsOpen(false)
  }

  const handleCreateButtonClick = () => {
    if (isFormInEditMode) {
      setExpertToEdit(undefined)
    }

    setFormModalIsOpen(true)
  }
  useEffect(() => {
    if (isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        page: 1,
      }))
    }

    if (!isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        take: 20,
        page: 1,
      }))
    }
  }, [isMinScreenWidthMD])

  return (
    <>
      <div>
        {!isMinScreenWidthLG && <NavigationSelect defaultRoute="expert" />}

        <div className="md:flex md:justify-between md:items-center md:mb-6">
          <h1 className="mb-4 font-medium text-grey-900 md:text-lg md:mb-0">
            Expert
          </h1>

          <div className="mb-4 md:flex md:gap-2">
            <SearchInput
              onSearch={handleSearchExpert}
              placeholder="Buscar por nome ou E-mail"
              className="mb-6 md:w-96 md:mb-0"
            />

            <Button className="h-10 ml-auto" onClick={handleCreateButtonClick}>
              Cadastrar
            </Button>
          </div>
        </div>

        {isMinScreenWidthMD ? (
          <TableWithManuallyPagination
            columns={columns}
            filters={filters}
            setFilters={setFilters}
          />
        ) : (
          <TableWithInfiniteScroll columns={columns} filters={filters} />
        )}
      </div>

      <DialogModal
        isOpen={formModalIsOpen}
        title={`${isFormInEditMode ? 'Editar' : 'Cadastrar'} expert`}
        onClose={handleCloseFormModal}
      >
        <ExpertForm
          isSubmitting={
            expertCreationMutation.isLoading || expertEditionMutation.isLoading
          }
          defaultValues={expertToEdit}
          onSubmit={handleSaveExpert}
        />
      </DialogModal>
    </>
  )
}
